import * as maptilersdk from '@maptiler/sdk'

export const TILE_GUARD_PROTOCOL = 'basemap-raster'

const FAILED_TTL = 5 * 60 * 1000
const FAILED_MAX = 2000

// Per-session registry of 4xx tile URLs: url -> timestamp of first failure
const failedTiles = new Map()

// Per-session set of 403-blocked layer prefixes (no TTL — auth doesn't
// change within a session). Keyed by stripping the last three path segments
// ({z}/{x}/{y}.ext) from the tile URL, leaving the layer-specific prefix.
const blockedLayers = new Set()

// Strip the trailing /{z}/{x}/{y}.ext from a tile URL to get the layer prefix.
// All tile URLs follow the standard {z}/{x}/{y} convention so three lastIndexOf
// passes reliably isolate the layer-specific part of the path.
function layerPrefix(url) {
  let s = url
  for (let i = 0; i < 3; i++) s = s.substring(0, s.lastIndexOf('/'))
  return s
}

// Generate a 1×1 transparent PNG synchronously via canvas (valid image, renders blank).
// MapLibre considers the tile "loaded" and stops retrying — no rAF cost.
function makeEmptyTile() {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = 1
    const dataUrl = canvas.toDataURL('image/png')
    const b64 = dataUrl.replace(/^data:image\/png;base64,/, '')
    const bin = atob(b64)
    const buf = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i)
    return buf.buffer
  } catch {
    return new ArrayBuffer(0)
  }
}

const EMPTY_TILE = makeEmptyTile()

let registered = false

export function registerTileGuardProtocol() {
  if (registered) return
  registered = true

  maptilersdk.addProtocol(TILE_GUARD_PROTOCOL, async (params, abortController) => {
    const realUrl = params.url.replace(`${TILE_GUARD_PROTOCOL}://`, 'https://')
    const prefix  = layerPrefix(realUrl)

    // Layer blocked for the session (previous 403 on this layer).
    // Return immediately — no network round-trip.
    if (blockedLayers.has(prefix)) return { data: EMPTY_TILE }

    // Per-URL cache for non-auth 4xx (404, 429, etc.).
    const failedAt = failedTiles.get(realUrl)
    if (failedAt !== undefined) {
      if (Date.now() - failedAt < FAILED_TTL) return { data: EMPTY_TILE }
      failedTiles.delete(realUrl)
    }

    const res = await fetch(realUrl, { signal: abortController.signal })

    if (res.status === 403) {
      // Unauthorized layer — block all tiles for this layer for the session.
      blockedLayers.add(prefix)
      return { data: EMPTY_TILE }
    }

    if (res.status >= 400 && res.status < 500) {
      if (failedTiles.size >= FAILED_MAX) {
        failedTiles.delete(failedTiles.keys().next().value)
      }
      failedTiles.set(realUrl, Date.now())
      return { data: EMPTY_TILE }
    }

    if (!res.ok) throw new Error(`tile ${res.status}`)
    return { data: await res.arrayBuffer() }
  })
}
